// authReturnTo — stashes the URL the user was on when they got bounced to
// /login so the re-auth flow can bring them back there instead of the
// default /home. Uses sessionStorage (not localStorage) so it's scoped to
// this browser tab and evaporates cleanly if the user closes the tab or
// switches accounts.
//
// Flow:
//   ProtectedRoute        →  captureIntendedRoute(location) on user=null redirect
//   Login (Continue Google) → left untouched: sessionStorage survives the OAuth
//                             top-level GET redirect back to our origin.
//   AuthCallback           →  consumeIntendedRoute() after a successful
//                             /auth/session exchange; falls back to "/home".
//
// The value is always a same-origin path (starts with "/") — we never trust
// or persist an absolute URL, so an attacker can't smuggle in a phishing
// redirect via a crafted link.

const KEY = "prairie.auth.returnTo";

const isSafeInternalPath = (p) => typeof p === "string" && p.startsWith("/") && !p.startsWith("//");

// Never resume onto the auth screens themselves — would create a loop.
// Also skip /home: it's the default destination anyway, so capturing it
// is meaningless AND masks bugs where the catch-all redirect to /home
// silently overwrites a real captured path.
const isAuthPath = (p) =>
  p === "/"
  || p === "/home"
  || p.startsWith("/login")
  || p.startsWith("/auth");

/**
 * Called by ProtectedRoute when it's about to redirect an unauthenticated
 * user to /login. Skips /login/AuthCallback themselves so we don't create a
 * self-referential loop.
 */
export function captureIntendedRoute(location) {
  try {
    const path = `${location.pathname}${location.search || ""}${location.hash || ""}`;
    if (!isSafeInternalPath(path)) return;
    if (isAuthPath(path)) return;
    sessionStorage.setItem(KEY, path);
  } catch {
    // sessionStorage disabled (private-mode Safari, cookie-blocked WebView) — silent.
  }
}

/**
 * Called by AuthCallback after a successful exchange. Returns the stashed
 * path (removing it in the process) or "/home" as the default.
 *
 * Re-validates the value against the same denylist as capture — defense-in-
 * depth against stale/tampered sessionStorage from an older build hijacking
 * a fresh sign-in.
 */
export function consumeIntendedRoute() {
  try {
    const v = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    if (!isSafeInternalPath(v)) return "/home";
    if (isAuthPath(v)) return "/home";
    return v;
  } catch {
    return "/home";
  }
}

/** Explicit clear — called from signOut so a leftover value from a prior
 *  bounce doesn't hijack the NEXT user's fresh sign-in. */
export function clearIntendedRoute() {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // storage disabled — nothing to clear.
  }
}
